/**
 * Collect File objects from a drag-drop DataTransfer, expanding any dropped
 * DIRECTORIES recursively (webkitGetAsEntry). This is how "上传文件夹" works
 * AI-native: the user drops files OR a folder and the system figures it out —
 * no "file vs folder" choice. A plain multi-file pick doesn't need this (the
 * input already yields a FileList); this is only for the drop path.
 */

/** Extensions the personal KB indexes today (mirrors the server default). */
export const SUPPORTED_UPLOAD_EXTS = new Set(["md", "txt", "markdown", "text", "docx", "html", "htm", "pdf"]);

/** Is this filename a supported upload type (case-insensitive)? */
export function isSupportedUpload(name: string): boolean {
  const i = name.lastIndexOf(".");
  return i >= 0 && SUPPORTED_UPLOAD_EXTS.has(name.slice(i + 1).toLowerCase());
}

/** Read one directory's entries fully (readEntries returns in batches). */
function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = [];
    const pump = (): void => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all);
          return;
        }
        all.push(...batch);
        pump();
      }, reject);
    };
    pump();
  });
}

/** Resolve one file entry to its File. */
function entryToFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/** Walk a filesystem entry, pushing every contained file into `out`. */
async function walk(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    out.push(await entryToFile(entry as FileSystemFileEntry));
  } else if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    const children = await readAllEntries(reader);
    for (const child of children) await walk(child, out);
  }
}

/**
 * Every file in a drop, expanding folders. Falls back to `dataTransfer.files`
 * when the entry API is unavailable (older browsers) — folders are then skipped
 * but loose files still upload.
 */
export async function collectDroppedFiles(dt: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    const entry = item.webkitGetAsEntry?.();
    if (entry !== null && entry !== undefined) entries.push(entry);
  }
  if (entries.length === 0) return Array.from(dt.files);
  const out: File[] = [];
  for (const entry of entries) await walk(entry, out);
  return out;
}
